import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

import popup_bg1 from '../../Common/Images/ImagesForAlert/popup_bg1.png';
import alert_close_n from '../../Common/Images/ImagesForAlert/alert_close_n.png';
import alert_close_o from '../../Common/Images/ImagesForAlert/alert_close_o.png';
import go_n from '../../Common/Images/go_n.gif';
import excel_icon from '../../Common/Images/Excel.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface Props {
    storeID: number;
    title: string;
    endDate: string | null;
    totalPurchases: number;
    onClose: () => void;
    showWarning: (msg: string) => void;
    showConfirm: (msg: string, onConfirm: () => void) => void;
}

interface PurchaseItem {
    RecNo: number;
    Surname: string;
    FirstName: string;
    Purchases: number;
    Phone: string;
    YearLevel: number;
    StudentEmail: string;
    Address: string;
    SentOn: string | null;
    PurchasedOn: string;
    Comments: string;
    Status: number;
    Code: string;
    SchoolCode: string;
    FamilyCode: string;
    Login: string;
    ItemType: string;
    sentChecked: boolean;   // local edit state, not persisted until Save
}

const EL_ManageStoresPurchases: React.FC<Props> = ({ storeID, title, endDate, totalPurchases, onClose, showWarning, showConfirm }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<PurchaseItem[]>([]);
    const [dateSelected, setDateSelected] = useState(false);

    const [fromDate, setFromDate] = useState<string>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 0);
        return d.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState<string>(() => {
        return new Date().toISOString().split('T')[0];
    });

    const [uniquePurchases, setUniquePurchases] = useState(0);

    const statusOptions = [
        { value: 0, label: '-Select-' },
        { value: 1, label: 'Sent' },
        { value: 2, label: 'Ordered' },
        { value: 3, label: 'Back Order' }
    ];

    useEffect(() => {
        loadPurchases(false);
    }, []);

    const loadPurchases = async (useDateFilter = false) => {
        setLoading(true);
        try {
            let url = `${API_BASE_URL}api/manage-store/get_purchases/?store_id=${storeID}`;

            if (useDateFilter) {
                url += `&start_date=${fromDate}&end_date=${toDate}`;
            }

            const res = await fetch(url);
            if (res.ok) {
                const json = await res.json();
                const withState: PurchaseItem[] = json.map((item: any) => ({
                    ...item,
                    sentChecked: !!item.SentOn
                }));
                setData(withState);
                setUniquePurchases(withState.length);
            } else {
                console.error("Failed to fetch");
            }
        } catch (e) {
            console.error(e);
            showWarning("Error loading purchases.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (index: number, field: keyof PurchaseItem, value: any) => {
        const newData = [...data];
        newData[index] = { ...newData[index], [field]: value };
        setData(newData);
    };

    const handleSentToggle = (index: number, checked: boolean) => {
        const newData = [...data];
        newData[index] = { ...newData[index], sentChecked: checked };
        setData(newData);
    };

    const handleSave = async () => {
        const updates = data.map(item => ({
            RecNo: item.RecNo,
            SchoolCode: item.SchoolCode,
            FamilyCode: item.FamilyCode,
            Login: item.Login,
            Comments: item.Comments || '',
            Status: item.Status,
            Code: item.Code || '',
            Sent: item.sentChecked,       // current checkbox state
            WasSent: !!item.SentOn        // state as originally loaded
        }));

        setLoading(true);

        try {
            const res = await fetch(
                `${API_BASE_URL}api/manage-store/${storeID}/`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'update_purchases', updates })
                }
            );

            const json = await res.json();

            if (res.ok && json.status === 'success') {
                showWarning("Saved Successfully");
                // FIX: preserve the active date filter on reload, like VB does
                await loadPurchases(dateSelected);
            } else {
                showWarning(json.error || json.message || "Error saving data.");
            }
        } catch (e) {
            console.error(e);
            showWarning("Network error while saving.");
        } finally {
            setLoading(false);
        }
    };

    const handleSendVouchers = async () => {
        showConfirm(
            "Are you sure you want to send vouchers for unsent items?",
            async () => {
                setLoading(true);
                try {
                    const body: any = { store_id: storeID };
                    if (dateSelected) {
                        body.start_date = fromDate;
                        body.end_date = toDate;
                    }

                    const res = await fetch(
                        `${API_BASE_URL}api/manage-store/send_voucher/`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(body)
                        }
                    );

                    const json = await res.json();

                    if (res.ok && json.status === 'success') {
                        showWarning(json.message || "Vouchers Sent Successfully");
                        await loadPurchases(dateSelected);
                    } else {
                        showWarning(json.error || json.message || "Failed to send vouchers.");
                    }
                } catch (e) {
                    console.error(e);
                    showWarning("Error sending vouchers.");
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const handleExport = () => {
        showWarning("Export functionality coming soon.");
    };

    const styles = {
        overlay: {
            position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
        },
        windowFrame: {
            width: '100%', height: '100%', backgroundColor: 'azure',
            display: 'flex', flexDirection: 'column' as const,
            boxShadow: '0 0 15px rgba(0,0,0,0.5)',
        },
        windowHeader: {
            height: '30px', backgroundImage: `url(${popup_bg1})`, backgroundRepeat: 'repeat-x',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingLeft: '10px', cursor: 'default', borderBottom: '1px solid #555',
        },
        windowTitleText: { fontFamily: 'Calibri', fontSize: '14pt', fontWeight: 'bold' as const, color: 'white' },
        contentArea: {
            flex: 1, display: 'flex', flexDirection: 'column' as const, padding: '0px',
            overflow: 'hidden', fontFamily: 'Arial', fontSize: '10pt',
        },
        pageHeader: {
            backgroundColor: 'Maroon', color: 'White', padding: '7px', fontWeight: 'bold' as const,
            textAlign: 'center' as const, marginTop: '20px', width: '95%', alignSelf: 'center'
        },
        infoTable: { width: '95%', margin: '10px auto', borderCollapse: 'collapse' as const },
        gridWrapper: {
            flex: 1, width: '95%', margin: '0 auto 10px auto', overflow: 'auto',
            border: '1px solid #999', backgroundColor: '#f0ffff'
        },
        gridTable: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '9pt' },
        th: {
            backgroundColor: '#C7DDFD', color: '#2859ac', padding: '4px', border: '1px solid #a3c0e8',
            textAlign: 'left' as const, whiteSpace: 'nowrap' as const,
            position: 'sticky' as const, top: 0, zIndex: 10
        },
        td: { padding: '3px', border: '1px solid #ccc', whiteSpace: 'nowrap' as const },
        footer: {
            padding: '10px', display: 'flex', gap: '10px', justifyContent: 'flex-start',
            width: '95%', margin: '0 auto 10px auto',
        },
        btn: {
            width: '120px', height: '26px', fontFamily: 'Arial', fontSize: '9pt',
            cursor: 'pointer', backgroundColor: '#f0f0f0', border: '1px solid #777'
        },
        input: { fontFamily: 'Trebuchet MS', fontSize: '9pt' }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.windowFrame}>
                {loading && <Loading />}

                <div style={styles.windowHeader}>
                    <span style={styles.windowTitleText}>Stores Purchases</span>
                    <img
                        src={alert_close_n}
                        alt="Close"
                        onClick={onClose}
                        style={{ cursor: 'pointer' }}
                        onMouseOver={e => e.currentTarget.src = alert_close_o}
                        onMouseOut={e => e.currentTarget.src = alert_close_n}
                    />
                </div>

                <div style={styles.contentArea}>
                    <div style={styles.pageHeader}>Store Item Purchases</div>

                    <table style={styles.infoTable}>
                        <tbody>
                            <tr>
                                <td style={{ width: '200px', paddingTop: '10px' }}>
                                    <b>Item Name:&nbsp;</b>{title}
                                </td>
                                <td style={{ width: '200px', paddingTop: '10px' }}>
                                    <b>End Date:</b>&nbsp;{endDate ? new Date(endDate).toLocaleDateString() : ''}
                                </td>
                                <td style={{ width: '50px', textAlign: 'right', padding: '3px' }}>From</td>
                                <td style={{ paddingLeft: '5px' }}>
                                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={styles.input} />
                                </td>
                            </tr>
                            <tr>
                                <td style={{ paddingTop: '5px' }}>
                                    <b>Total Purchases:</b>&nbsp;{totalPurchases}
                                </td>
                                <td style={{ paddingTop: '5px' }}>
                                    <b>Unique Purchases:</b>&nbsp;{uniquePurchases}
                                </td>
                                <td style={{ textAlign: 'right', padding: '3px' }}>To</td>
                                <td style={{ paddingLeft: '5px', display: 'flex', alignItems: 'center' }}>
                                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={styles.input} />
                                    <img
                                        src={go_n}
                                        alt="Go"
                                        onClick={() => {
                                            setDateSelected(true);
                                            loadPurchases(true);
                                        }}
                                        style={{ cursor: 'pointer', marginLeft: '10px', verticalAlign: 'middle' }}
                                    />
                                    {title && title.includes("Starlight") && (
                                        <img
                                            src={excel_icon}
                                            alt="Export"
                                            onClick={handleExport}
                                            style={{ cursor: 'pointer', marginLeft: '10px', verticalAlign: 'middle' }}
                                        />
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={styles.gridWrapper}>
                        <table style={styles.gridTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>Surname</th>
                                    <th style={styles.th}>First Name</th>
                                    <th style={styles.th}>Purchases</th>
                                    <th style={styles.th}>Phone</th>
                                    <th style={styles.th}>Year</th>
                                    <th style={styles.th}>Email</th>
                                    <th style={styles.th}>Address</th>
                                    <th style={styles.th}>Sent</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Comments</th>
                                    <th style={styles.th}>Date purchased</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Code</th>
                                    <th style={styles.th}>School Code</th>
                                    <th style={styles.th}>Family Code</th>
                                    <th style={styles.th}>Login</th>
                                    <th style={styles.th}>ReSend</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, i) => (
                                    <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#EFF3FB' }}>
                                        <td style={styles.td}>{i + 1}</td>
                                        <td style={styles.td}>{row.Surname}</td>
                                        <td style={styles.td}>{row.FirstName}</td>
                                        <td style={styles.td}>{row.Purchases}</td>
                                        <td style={styles.td}>{row.Phone}</td>
                                        <td style={styles.td}>{row.YearLevel}</td>
                                        <td style={styles.td}>{row.StudentEmail}</td>
                                        <td style={styles.td} title={row.Address}>
                                            {row.Address.length > 25 ? row.Address.substring(0, 25) + '...' : row.Address}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={row.sentChecked}
                                                onChange={(e) => handleSentToggle(i, e.target.checked)}
                                            />
                                        </td>
                                        <td style={styles.td}>{row.SentOn || ''}</td>
                                        <td style={styles.td}>
                                            <input
                                                type="text"
                                                value={row.Comments || ''}
                                                onChange={(e) => handleInputChange(i, 'Comments', e.target.value)}
                                                style={{ width: '95%', fontFamily: 'Arial', fontSize: '9pt' }}
                                            />
                                        </td>
                                        <td style={styles.td}>{row.PurchasedOn}</td>
                                        <td style={styles.td}>
                                            <select
                                                value={row.Status || 0}
                                                onChange={(e) => handleInputChange(i, 'Status', parseInt(e.target.value))}
                                                style={{ width: '100px', fontFamily: 'Arial', fontSize: '9pt' }}
                                            >
                                                {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </td>
                                        <td style={styles.td}>
                                            <input
                                                type="text"
                                                value={row.Code || ''}
                                                disabled={row.Status !== 3}
                                                onChange={(e) => handleInputChange(i, 'Code', e.target.value)}
                                                style={{ width: '100px', fontFamily: 'Arial', fontSize: '9pt' }}
                                            />
                                        </td>
                                        <td style={styles.td}>{row.SchoolCode}</td>
                                        <td style={styles.td}>{row.FamilyCode}</td>
                                        <td style={styles.td}>{row.Login}</td>
                                        <td style={styles.td}>
                                            {row.SentOn && (
                                                <span
                                                    onClick={() => showWarning('Resend functionality to be linked')}
                                                    style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                                                >
                                                    ReSend
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={styles.footer}>
                        <button onClick={onClose} style={styles.btn}>Close</button>
                        <button onClick={handleSave} style={styles.btn}>Save</button>
                        <button
                            onClick={handleSendVouchers}
                            style={{ ...styles.btn, width: '250px' }}
                            disabled={data.length === 0}
                        >
                            Send Unsent Vouchers
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EL_ManageStoresPurchases;